import React, { useState, useRef, useEffect } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { IconButton, Menu, MenuItem } from '@mui/material';
import ProjectIcon from '../../../assets/project-icon.png';
import { getSignedURLs } from '../../../utils/helpers';

import './styles.css';

const formatDate = (creationDate) => {
	try {
		const d = creationDate?.toDate ? creationDate.toDate() : new Date(creationDate);
		return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	} catch {
		return '';
	}
};

const ProjectCard = ({ project, organizationId, onEdit, onDelete, onRename }) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [isRenaming, setIsRenaming] = useState(false);
	const [newName, setNewName] = useState(project.name);
	const [thumbnailURL, setThumbnailURL] = useState(null);
	const menuJustClosed = useRef(false);
	const open = Boolean(anchorEl);

	const firstImage = project.images?.[0];

	// Load thumbnail signed URL
	useEffect(() => {
		if (firstImage && project.id) {
			const key = `${project.id}/images/${firstImage.fileName}`;
			getSignedURLs(project.id, [key])
				.then(urls => setThumbnailURL(urls[key] || null))
				.catch(err => {
					console.error('Error loading thumbnail:', err);
					setThumbnailURL(null);
				});
		} else {
			setThumbnailURL(null);
		}
	}, [firstImage, project.id]);

	const handleCardClick = () => {
		if (isRenaming || menuJustClosed.current) return;
		onEdit(project);
	};

	const handleMenuOpen = (e) => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
	};

	const handleMenuClose = () => {
		menuJustClosed.current = true;
		setAnchorEl(null);
		setTimeout(() => { menuJustClosed.current = false; }, 0);
	};

	const handleRenameSubmit = () => {
		if (newName.trim() && newName.trim() !== project.name) {
			onRename(project, newName.trim());
		}
		setIsRenaming(false);
	};

	const handleRenameKeyDown = (e) => {
		if (e.key === 'Enter') handleRenameSubmit();
		if (e.key === 'Escape') setIsRenaming(false);
	};

	return (
		<div className="proj-card" onClick={handleCardClick}>
			{/* Thumbnail */}
			<div className="proj-card__thumb">
				<img
					src={thumbnailURL || ProjectIcon}
					alt=""
					className="proj-card__thumb-img"
					onError={(e) => { e.currentTarget.src = ProjectIcon; }}
				/>
				{thumbnailURL && <span className="proj-card__360-badge">360°</span>}
			</div>

			{/* Body */}
			<div className="proj-card__body">
				{isRenaming ? (
					<div className="proj-card__rename" onClick={(e) => e.stopPropagation()}>
						<input
							className="proj-card__rename-input"
							autoFocus
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
							onKeyDown={handleRenameKeyDown}
						/>
						<div className="proj-card__rename-actions">
							<button className="proj-card__rename-save" onClick={handleRenameSubmit}>Save</button>
							<button className="proj-card__rename-cancel" onClick={() => setIsRenaming(false)}>Cancel</button>
						</div>
					</div>
				) : (
					<p className="proj-card__name">{project.name}</p>
				)}
				<div className="proj-card__meta">
					<span className="proj-card__meta-item">
						<ImageOutlinedIcon className="proj-card__meta-icon" />
						{project.images?.length ?? 0} {(project.images?.length ?? 0) === 1 ? 'image' : 'images'}
					</span>
					{project.creationDate && (
						<span className="proj-card__meta-item">
							<CalendarTodayOutlinedIcon className="proj-card__meta-icon" />
							{formatDate(project.creationDate)}
						</span>
					)}
				</div>
			</div>

			{/* Kebab menu */}
			{!isRenaming && (
				<div className="proj-card__actions" onClick={(e) => e.stopPropagation()}>
					<IconButton
						size="small"
						onClick={handleMenuOpen}
						className="proj-card__menu-btn"
						aria-label="Section options"
					>
						<MoreVertIcon fontSize="small" />
					</IconButton>
					<Menu
						anchorEl={anchorEl}
						open={open}
						onClose={handleMenuClose}
						PaperProps={{
							sx: { borderRadius: '8px', boxShadow: '0 4px 16px rgba(15,45,78,0.14)', minWidth: 140 },
						}}
					>
						<MenuItem
							onClick={(e) => {
								e.stopPropagation();
								setIsRenaming(true);
								setNewName(project.name);
								handleMenuClose();
							}}
							sx={{ fontSize: '13px', fontFamily: 'Open Sans, sans-serif', color: '#0f2d4e' }}
						>
							Rename
						</MenuItem>
						<MenuItem
							onClick={(e) => {
								e.stopPropagation();
								onDelete(project);
								handleMenuClose();
							}}
							sx={{ fontSize: '13px', fontFamily: 'Open Sans, sans-serif', color: '#c62828' }}
						>
							Delete
						</MenuItem>
					</Menu>
				</div>
			)}
		</div>
	);
};

export default ProjectCard;
